import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { FileText } from 'lucide-react';

export const TermsOfService = () => {
  return (
    <div data-testid="terms-of-service-page">
      <Helmet>
        <title>Terms of Service | A-Quality Pool Service</title>
        <meta name="description" content="A-Quality Pool Service terms of service. Review the terms and conditions for using our pool service and website." />
        <link rel="canonical" href="https://a-qualitypools.net/terms" />
      </Helmet>
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex items-center bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
              <FileText className="h-5 w-5 text-sky-400 mr-2" />
              <span className="text-white font-medium">Legal Agreement</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">Terms of Service</h1>
            <p className="text-slate-300">Last Updated: April 15, 2026</p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 prose prose-slate prose-lg max-w-none">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">1. Acceptance of Terms</h2>
            <p className="text-slate-600 mb-4">
              By accessing and using the A-Quality Pool Service website and services, you accept and agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our website or services. A-Quality Pool Service is a Cody Pools Company, fully licensed and insured (License # CPC1460475).
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">2. Services Description</h2>
            <p className="text-slate-600 mb-4">
              A-Quality Pool Service provides professional pool maintenance, repair, renovation, and related services in the Central and Gulf Coast Florida area. Our services include but are not limited to:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Residential and commercial pool cleaning and maintenance</li>
              <li>Pool equipment repair and installation (pumps, heaters, filters, plumbing)</li>
              <li>Pool resurfacing, tile and coping, and renovation services</li>
              <li>LED lighting, water features, and deck resurfacing</li>
              <li>Leak detection and emergency pool repair</li>
            </ul>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">3. Service Agreements</h2>
            <p className="text-slate-600 mb-4">
              Specific service terms, pricing, and schedules will be outlined in individual service agreements provided at the time of service. These Terms of Service apply in addition to any individual service agreement. In the event of a conflict, the individual service agreement shall take precedence.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">4. Quotes and Pricing</h2>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>All quotes provided through our website or by our representatives are estimates and may be subject to change based on actual conditions encountered during service.</li>
              <li>Pricing is based on the scope of work agreed upon. Any additional work beyond the original scope will be discussed and approved before proceeding.</li>
              <li>Promotional offers and discounts are subject to their stated terms and conditions and cannot be combined with other offers unless explicitly stated.</li>
            </ul>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">5. Payment Terms</h2>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Payment terms will be outlined in your individual service agreement.</li>
              <li>For recurring maintenance services, payment is due as specified in your service agreement.</li>
              <li>For repair and renovation services, a deposit may be required prior to commencement of work, with the balance due upon completion.</li>
              <li>Late payments may be subject to additional fees as outlined in your service agreement.</li>
            </ul>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">6. Cancellation and Rescheduling</h2>
            <p className="text-slate-600 mb-4">
              We understand that plans change. Please provide at least 24 hours notice for cancellation or rescheduling of service appointments. Repeated cancellations without adequate notice may result in a cancellation fee. Cancellation of recurring service contracts should be submitted in writing with 30 days notice.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">7. Customer Responsibilities</h2>
            <p className="text-slate-600 mb-4">As a customer, you agree to:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Provide safe and reasonable access to your pool and equipment for service personnel</li>
              <li>Ensure pets are secured during scheduled service visits</li>
              <li>Inform us of any known hazards or issues related to your pool or property</li>
              <li>Maintain pool water levels at the appropriate level between service visits</li>
              <li>Notify us promptly of any concerns or issues with our services</li>
            </ul>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">8. Warranties and Guarantees</h2>
            <p className="text-slate-600 mb-4">
              A-Quality Pool Service stands behind our work. Specific warranty terms vary by service type and will be communicated at the time of service. General warranty provisions include:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Workmanship is warranted for a period specified in your service agreement.</li>
              <li>Equipment warranties are subject to the manufacturer's warranty terms.</li>
              <li>Warranty claims must be reported promptly to allow for timely inspection and resolution.</li>
            </ul>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">9. Limitation of Liability</h2>
            <p className="text-slate-600 mb-4">
              To the fullest extent permitted by law, A-Quality Pool Service shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from or related to the use of our website or services. Our total liability for any claim shall not exceed the amount paid for the specific service giving rise to the claim.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">10. Website Use</h2>
            <p className="text-slate-600 mb-4">When using our website, you agree not to:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Use the website for any unlawful purpose</li>
              <li>Attempt to gain unauthorized access to any part of the website</li>
              <li>Submit false or misleading information through our contact forms</li>
              <li>Reproduce, distribute, or modify any content without our written consent</li>
              <li>Use automated tools to scrape or collect data from the website</li>
            </ul>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">11. Intellectual Property</h2>
            <p className="text-slate-600 mb-4">
              All content on this website, including text, images, logos, graphics, and design, is the property of A-Quality Pool Service or its licensors and is protected by applicable intellectual property laws. You may not use, reproduce, or distribute any content without our prior written consent.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">12. Force Majeure</h2>
            <p className="text-slate-600 mb-4">
              A-Quality Pool Service shall not be liable for delays or failure to perform due to circumstances beyond our reasonable control, including but not limited to natural disasters, severe weather, pandemics, supply chain disruptions, or government actions.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">13. Governing Law</h2>
            <p className="text-slate-600 mb-4">
              These Terms of Service are governed by and construed in accordance with the laws of the State of Florida. Any disputes arising under these terms shall be subject to the exclusive jurisdiction of the courts in Pasco County, Florida.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">14. Changes to Terms</h2>
            <p className="text-slate-600 mb-4">
              We reserve the right to modify these Terms of Service at any time. Changes will be effective immediately upon posting to this page. Your continued use of our website and services after any changes constitutes acceptance of the modified terms.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mt-8 mb-4">15. Contact Us</h2>
            <p className="text-slate-600 mb-4">
              If you have any questions about these Terms of Service, please contact us:
            </p>
            <div className="bg-slate-50 rounded-xl p-6 mb-4">
              <p className="text-slate-700 font-semibold">A-Quality Pool Service</p>
              <p className="text-slate-600">A Cody Pools Company</p>
              <p className="text-slate-600">Phone: 813-453-5988</p>
              <p className="text-slate-600">Email: info@a-qualitypools.net</p>
              <p className="text-slate-600">License # CPC1460475</p>
            </div>

          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default TermsOfService;
